/** Therapist voice command — Phase 1–5 types */

export type MicOwner = 'VOICE_COMMAND' | 'SESSION_DICTATION' | 'MILESTONE_RECORDER' | null;

export type VoiceAgentState =
  | 'idle'
  | 'starting'
  | 'listening'
  | 'finalizing'
  | 'understanding'
  | 'executing'
  | 'success'
  | 'clarification'
  | 'error';

/** Phase 1–5 voice actions (nav / open wizards / schedule read — no mutations). */
export type TherapistVoiceAction =
  | 'NAVIGATE_BACK'
  | 'NAVIGATE_HOME'
  | 'SHOW_CLIENTS'
  | 'SHOW_ASSIGNED_CLIENTS'
  | 'OPEN_CLIENT'
  | 'OPEN_CLIENT_DASHBOARD'
  | 'OPEN_SESSIONS'
  | 'OPEN_CARE_PLAN'
  | 'CREATE_CARE_PLAN'
  | 'OPEN_GOALS'
  | 'ADD_GOAL'
  | 'OPEN_GOAL_COACH'
  | 'OPEN_ASSESSMENTS'
  | 'OPEN_DOCUMENTS'
  | 'OPEN_MESSAGES'
  | 'OPEN_CALENDAR'
  | 'CREATE_CLIENT'
  | 'OPEN_PROFILE'
  | 'SHOW_SCHEDULE'
  | 'SHOW_APPOINTMENTS'
  | 'SHOW_NEXT_APPOINTMENT'
  | 'CANCEL'
  | 'STOP_LISTENING';

export type VoiceKidCandidate = {
  id: string;
  name: string;
  score: number;
};

/** Relative date token resolved client-side (Phase 4). */
export type VoiceDateExpression =
  | 'today'
  | 'tomorrow'
  | 'next'
  | 'weekday'
  | 'week'
  | null;

export type VoiceIntent = {
  action: TherapistVoiceAction;
  confidence: number;
  needsClarification: boolean;
  clarificationReason?: string;
  statusMessage: string;
  kidProfileId?: string;
  spokenName?: string;
  candidates?: VoiceKidCandidate[];
  /** Phase 4 — ISO date YYYY-MM-DD when targeting a day on /book-meeting */
  dateYmd?: string;
  dateExpression?: VoiceDateExpression;
  /** Weekday 0=Sun … 6=Sat when dateExpression === 'weekday' */
  weekday?: number;
  /** Short real-data summary for the voice status card (not a fake schedule UI). */
  scheduleSummary?: string;
};

export type VoicePerfMarks = {
  micTapAt?: number;
  recordingStartedAt?: number;
  speechEndedAt?: number;
  intentResolvedAt?: number;
  actionExecutedAt?: number;
};

export const CLINICAL_VOICE_ROLES = [
  'THERAPIST',
  'INTERNS',
  'CLINICIAN',
  'ORG_ADMIN',
] as const;

export function isClinicalVoiceRole(role: string | null | undefined): boolean {
  if (!role) return false;
  return (CLINICAL_VOICE_ROLES as readonly string[]).includes(role.toUpperCase());
}

/**
 * Feature flag for therapist Talk / voice commands.
 * Default OFF (production-safe). Vikrant/local: set
 *   VITE_THERAPIST_VOICE_COMMANDS_ENABLED=true
 * then restart `npm run dev`.
 */
export function isTherapistVoiceCommandsEnabled(): boolean {
  const raw = (
    import.meta as ImportMeta & {
      env?: { VITE_THERAPIST_VOICE_COMMANDS_ENABLED?: string };
    }
  ).env?.VITE_THERAPIST_VOICE_COMMANDS_ENABLED;
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
}

/** Routes where session/group dictation owns the mic UI — hide command Talk control. */
export function isSessionDictationRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/session-group/') ||
    pathname.startsWith('/session-detail/')
  );
}

/** Kid-scoped workspace actions that need an active / named client. */
export function isKidScopedVoiceAction(action: TherapistVoiceAction): boolean {
  switch (action) {
    case 'OPEN_CLIENT':
    case 'OPEN_CLIENT_DASHBOARD':
    case 'OPEN_SESSIONS':
    case 'OPEN_CARE_PLAN':
    case 'CREATE_CARE_PLAN':
    case 'OPEN_GOALS':
    case 'ADD_GOAL':
    case 'OPEN_GOAL_COACH':
    case 'OPEN_ASSESSMENTS':
    case 'OPEN_DOCUMENTS':
    case 'OPEN_MESSAGES':
      return true;
    default:
      return false;
  }
}

export function isScheduleVoiceAction(action: TherapistVoiceAction): boolean {
  return (
    action === 'SHOW_SCHEDULE' ||
    action === 'SHOW_APPOINTMENTS' ||
    action === 'SHOW_NEXT_APPOINTMENT'
  );
}

/** After we hear words, stop this long after the last update. */
export const VOICE_SILENCE_MS = 2500;
/** Max listen if no words arrive — UI always clears (stop is sync, never hangs). */
export const VOICE_MAX_LISTEN_MS = 15000;
/** Tier3 semantic AI timeout (Phase 5). */
export const VOICE_TIER3_TIMEOUT_MS = 8000;
export const VOICE_DEBUG =
  typeof import.meta !== 'undefined' &&
  Boolean((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV);
